import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { App } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { RowSelectionState } from '@tanstack/react-table'
import { useQueryClient } from '@tanstack/react-query'

import { EmailInviteStatusEnum } from '@vacationtracker/shared/types/user'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'

import * as logger from '../../../../services/logger'
import { track } from '../../../../services/analytics/analytics'
import { postCoreEvent } from '../../../../services/core-event'
import { queryKeys } from '../../../../services/tanstack-query'
import { notificationStore } from '../../../../context/notificationsContext/store'
import { useAppSelector } from '../../../../store/hooks'
import { useShouldEnableFeatures } from '../../../../store/use-should-enable-features'
import { selectAuthUserSlice } from '../../../../store/auth-user-slice'
import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { selectDateFormatSlice } from '../../../../store/date-format-slice'
import IntlMessages from '../../../../util/IntlMessages'
import { ConfirmModal } from '../../../../design-system/ConfirmModal'

import { IUserFilteredInfo, IUserStatusChangedBody } from '../../../../types/users'
import { IGetLeaveTypeList } from '../../../../types/custom-queries'
import { IGetTeamsShort } from '../../../../types/teams'
import { IGetLocationsShort } from '../../../../types/locations'
import { IUsersFilter, UsersPageViewTab } from '../types'
import { showApiErrorNotification } from '../showApiErrorNotification'
import { usePermissions } from '../hooks/usePermissions'
import { useUsersInfinite } from '../hooks/useUsersInfinite'
import { useLeaveSummaries } from '../hooks/useLeaveSummaries'
import { useOptimisticUserEvent } from '../hooks/useOptimisticUserEvent'
import { IUseColumnPrefsResult } from '../hooks/useColumnPrefs'
import { IUsersReferenceData } from '../hooks/useUsersReferenceData'
import { createEditingContext, ILocationDeleteSuggestion } from '../columns/editingContext'
import { departmentMoveTargets, locationMoveTargets } from '../columns/moveTargetOptions'
import { COLUMN_IDS, columnLabelId } from '../columns/columnIds'
import { selectAllStillHolds, showsLoadedOnlyNote, type ISelectionScope } from './selectAllScope'
import { fitColumnWidths } from '../columns/fitColumnWidths'
import { buildProfileColumns, PROFILE_COLUMN_WIDTHS } from '../columns/profileColumns'
import { buildLeaveDetailsColumns, leaveDetailsColumnGroups, leaveDetailsColumnWidths, leaveDetailsNumericColumnIds } from '../columns/leaveDetailsColumns'
import { ADP_FLAG_MESSAGE_ID } from '../../../../components/adp/integrated-flag'
import { inviteBadgeFor } from './NameCell'
import { RowMenuActionKey } from './RowActionsMenu'
import { UsersTable } from './UsersTable'
import { UsersTableEditingProvider } from './EditableCell'
import { BulkActionsBar } from './BulkActionsBar'
import { EditUserModal } from './EditUserModal'
import { QuotaEditDialog } from './QuotaEditDialog'

const LEAVE_DETAILS_PINNED_START = [COLUMN_IDS.name]

/** The columns sized to their own content, which therefore wrap rather than truncate at the cap. */
const FITTED_TEXT_COLUMN_IDS = [COLUMN_IDS.name, COLUMN_IDS.team, COLUMN_IDS.location, COLUMN_IDS.email, COLUMN_IDS.employeeId]

export interface IUsersTableSectionProps {
  /** Host-owned. Memoize it: this feeds the query key. */
  filters: IUsersFilter
  reference: IUsersReferenceData
  /**
   * Host-owned, because the export modal reads the visible column order too. Only the two read
   * members are required, so a host with no column-config control can pass a fixed arrangement.
   */
  columnPrefs: Pick<IUseColumnPrefsResult, 'columnOrder' | 'columnVisibility'>
  view?: UsersPageViewTab
  leaveDetails?: {
    activeLeaveTypes: IGetLeaveTypeList[]
    columnPrefs: IUseColumnPrefsResult
    displayUnit: 'days' | 'hours'
    activated: boolean
    /** Puts what is left under the used days on capped rows. */
    showBalance?: boolean
  }
  density?: 'comfortable' | 'compact'
  /**
   * Set when every row is in one location. Hides the location column - a wall of the same value,
   * and one that could otherwise be edited out from under the list - and keeps that location out
   * of the bulk-move targets.
   */
  locationScope?: { locationId: string; moveLabelId?: string }
  energyEnabled: boolean
  /**
   * Whether the host is currently showing this section. A tab host keeps its inactive panes
   * mounted and hides them, which the body-portalled bulk bar escapes - so it has to be
   * told, rather than inferring it from a DOM it sits outside of.
   */
  active?: boolean
  /**
   * Invite state, for the surfaces that have it. It defaults to accepted because an empty
   * invite map reads as "uninvited" for everyone, painting badges and offering an action the
   * caller cannot service.
   */
  invites?: {
    getStatus: (user: IUserFilteredInfo) => EmailInviteStatusEnum
    onInvite?: (user: IUserFilteredInfo) => void
  }
  /**
   * The delete-suggestion pair, passed together or not at all. A location's own Users tab omits
   * both - offering to delete the location you are standing on is not a kindness.
   */
  fetchLocationDeleteSuggestion?: (params: { locationId?: string; affectedUserWasActive: boolean }) => Promise<ILocationDeleteSuggestion | null>
  onSuggestLocationDelete?: (suggestion: ILocationDeleteSuggestion) => void
  onActiveCountDelta?: (status: string, count: number) => void
  /** How many users match the filters, against which the footer counts the rows loaded so far.
   * Undefined while the count is still unknown, and the footer then reports only what it has. */
  totalCount?: number
}

export const UsersTableSection = (props: IUsersTableSectionProps): React.ReactElement => {
  const {
    filters,
    reference,
    columnPrefs,
    view = 'profile',
    leaveDetails,
    locationScope,
    energyEnabled,
    active = true,
    invites,
    fetchLocationDeleteSuggestion,
    onSuggestLocationDelete,
    onActiveCountDelta,
    totalCount,
    density,
  } = props

  const getInviteStatus = invites?.getStatus ?? (() => EmailInviteStatusEnum.accepted)
  // Primitives, not the object: hosts build locationScope inline, so anything keyed on its identity
  // rebuilds every render - which would hand TanStack fresh column definitions each time.
  const scopedLocationId = locationScope?.locationId
  const scopedMoveLabelId = locationScope?.moveLabelId
  // Both mounts computed this identically; it is a selector, not a decision the host makes.
  const showLabels = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.labels)

  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const queryClient = useQueryClient()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { dateFormat } = useAppSelector(selectDateFormatSlice)
  const { actionNotifications, eventsNotifications, addActionNotifications } = useContext(notificationStore)

  const permissions = usePermissions()
  const { amIAdmin, isFullAdmin, isLocationAdmin, isMicrosoftBillingPayment, canEditUser, canEditCell } = permissions

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [editUser, setEditUser] = useState<IUserFilteredInfo | null>(null)
  const [quotaEdit, setQuotaEdit] = useState<{ userId: string; leaveTypeId: string } | null>(null)
  const [confirmState, setConfirmState] = useState<{
    title: string
    message: string
    okLabel: string
    danger?: boolean
    onOk: () => void
  } | null>(null)

  const usersQuery = useUsersInfinite(filters, energyEnabled)
  const { users } = usersQuery

  const userIds = useMemo(() => users.map((user) => user.id), [users])
  const leaveActivated = Boolean(leaveDetails?.activated)
  const leaveSummaries = useLeaveSummaries(leaveActivated ? userIds : [], leaveActivated)

  const isFirstNotificationRun = useRef(true)
  useEffect(() => {
    if (isFirstNotificationRun.current) {
      isFirstNotificationRun.current = false
      return
    }
    queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() })
  }, [eventsNotifications])

  // This table's own mutations register correlationIds in actionNotifications; the websocket removes
  // them once the backend projection finishes. Invalidate on removal, never on insertion - a refetch
  // fired at insertion would read pre-projection data and clobber the optimistic patch.
  const previousActionNotifications = useRef<string[]>(actionNotifications)
  useEffect(() => {
    const previous = previousActionNotifications.current
    previousActionNotifications.current = actionNotifications
    if (previous.some((correlationId) => !actionNotifications.includes(correlationId))) {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() })
    }
  }, [actionNotifications])

  // A new filter or view replaces what the table is showing; a selection made against the old set
  // has no meaning against the new one.
  useEffect(() => {
    setRowSelection({})
  }, [filters, view])

  const showSelection = amIAdmin && !isMicrosoftBillingPayment && filters.status !== 'DELETED' && view === 'profile'
  const canSelectRow = (user: IUserFilteredInfo) => user.role !== 'Admin' && user.status !== 'DELETED' && canEditUser(user)
  const selectedCount = Object.values(rowSelection).filter(Boolean).length

  // Select all reaches only the rows paging has loaded. Remembering that it was Select all, rather
  // than forty individual ticks, is what lets the confirmations say so - and unticking any one row
  // makes it an ordinary selection again, which needs no such warning.
  const [viaSelectAll, setViaSelectAll] = useState(false)
  const selectAllPending = useRef(false)
  const changeRowSelection = (next: RowSelectionState) => {
    setViaSelectAll(selectAllPending.current || selectAllStillHolds({ wasSelectAll: viaSelectAll, previous: rowSelection, next }))
    selectAllPending.current = false
    setRowSelection(next)
  }
  const selectionScope: ISelectionScope | undefined = totalCount === undefined ? undefined : { loaded: users.length, total: totalCount, viaSelectAll }
  const loadedOnlyNote =
    showsLoadedOnlyNote(selectionScope) && selectionScope ? formatMessage({ id: 'users.bulk.loadedOnlyNote' }, { count: selectedCount, total: selectionScope.total }) : ''

  useEffect(() => {
    setRowSelection((previous) => {
      const selectedIds = Object.keys(previous).filter((id) => previous[id])
      if (selectedIds.length === 0) {
        return previous
      }
      const loadedIds = new Set(users.map((user) => user.id))
      const kept = selectedIds.filter((id) => loadedIds.has(id))
      return kept.length === selectedIds.length ? previous : Object.fromEntries(kept.map((id) => [id, true]))
    })
  }, [users])

  const postUserEvent = async (params: { eventBody: Record<string, unknown>; progressMessageId: string; errorType: string }): Promise<boolean> => {
    const { eventBody, progressMessageId, errorType } = params
    try {
      const response = await postCoreEvent(eventBody, 'users-table')
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: progressMessageId }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      addActionNotifications([response.correlationId])
      return true
    } catch (error) {
      logger.warning({ errorType, error })
      showApiErrorNotification(notification, formatMessage, error)
      return false
    }
  }

  const updateUser = async (userId: string, data: IUserStatusChangedBody) => {
    const posted = await postUserEvent({
      eventBody: { eventType: 'USER_STATUS_CHANGED', eventGroup: 'USER', userId, ...data },
      progressMessageId: 'user.status.updateInProgress',
      errorType: 'ERROR UPDATE USER',
    })
    if (posted) {
      usersQuery.removeUser(userId)
      onActiveCountDelta?.(data.status, 1)
    }
    setRowSelection({})
    return posted
  }

  const updateUserAndSuggestLocationDelete = async (userId: string, userData: IUserStatusChangedBody, user: IUserFilteredInfo) => {
    // Kick the count query before the mutation: shouldSuggestLocationDelete expects the pre-mutation active count
    const suggestionPromise = fetchLocationDeleteSuggestion
      ? fetchLocationDeleteSuggestion({ locationId: user.location?.id, affectedUserWasActive: user.status === 'ACTIVE' })
      : Promise.resolve(null)
    const updated = await updateUser(userId, userData)
    const suggestion = updated ? await suggestionPromise : null
    if (suggestion) {
      onSuggestLocationDelete?.(suggestion)
    }
  }

  const changeUsersStatus = async (status: string) => {
    const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id])
    const posted = await postUserEvent({
      eventBody: {
        eventType: 'BULK_USER_STATUS',
        eventGroup: 'BULK_ACTIONS',
        users: selectedIds.map((userId) => ({ userId, status })),
      },
      progressMessageId: 'user.bulkChangeStatusInProgress',
      errorType: 'ERROR CHANGE USERS STATUS',
    })
    if (posted) {
      selectedIds.forEach((userId) => usersQuery.removeUser(userId))
      onActiveCountDelta?.(status, selectedIds.length)
      setRowSelection({})
    }
  }

  const handleRowMenuAction = (key: RowMenuActionKey, user: IUserFilteredInfo) => {
    const userData: IUserStatusChangedBody = {
      status: 'ACTIVE',
      platform: user.platform,
    }

    if (key === 'inviteUser') {
      invites?.onInvite?.(user)
    }

    if (key === 'deactivateUser') {
      userData.status = 'INACTIVE'
      setConfirmState({
        title: formatMessage({ id: 'users.table.statusTitle' }, { name: user.name }),
        message: formatMessage({ id: 'users.table.deactivateConsequence' }, { name: user.name }),
        okLabel: formatMessage({ id: 'users.deactivate' }),
        danger: true,
        onOk: () => updateUserAndSuggestLocationDelete(user.id, userData, user),
      })
    }
    if (key === 'editUser') {
      setEditUser(user)
    }
    if (key === 'deleteUser') {
      userData.status = 'DELETED'
      setConfirmState({
        title: formatMessage({ id: 'app.deleteUser' }),
        message: formatMessage({ id: 'users.deleteUserUserConfirm' }, { name: user.name }),
        okLabel: formatMessage({ id: 'components.modal.confirm' }),
        danger: true,
        onOk: () => updateUserAndSuggestLocationDelete(user.id, userData, user),
      })
    }
    if (key === 'activateUser') {
      setConfirmState({
        title: formatMessage({ id: 'users.table.statusTitle' }, { name: user.name }),
        message: formatMessage({ id: 'users.table.reactivateConsequence' }, { name: user.name }),
        okLabel: formatMessage({ id: 'app.activate' }),
        onOk: () => updateUser(user.id, userData),
      })
    }
  }

  const optimisticPost = useOptimisticUserEvent({ patchUser: usersQuery.patchUser })

  const teamOptions = useMemo(() => reference.teams.map((team) => ({ id: team.id, name: team.name })), [reference.teams])
  const locationOptions = useMemo(() => reference.locations.map((loc) => ({ id: loc.id, name: loc.name })), [reference.locations])
  const noCountryLabel = formatMessage({ id: 'locationPlace.card.noCountrySet' })
  const moveTargets = useMemo(
    () => ({ teams: departmentMoveTargets(reference.teams), locations: locationMoveTargets(reference.locations, noCountryLabel) }),
    [reference.teams, reference.locations, noCountryLabel]
  )

  // The header checkbox calls this just before TanStack reports the new selection, so the change
  // that follows is known to be a Select all rather than a row tick that happened to catch them all.
  const trackSelectAll = () => {
    selectAllPending.current = true
    track('BULK_SELECT_ALL', {
      platform: authUser.platform,
      userId: authUser.id,
      usersCount: users.length,
    })
  }

  // Column defs are memoized below; function props read the CURRENT render's values
  // through this ref so the memoized closures never act on stale state.
  const latest = useRef({
    canEditCell,
    getLockReason: permissions.getLockReason,
    getInviteStatus,
    rowSelection,
    handleRowMenuAction,
    optimisticPost,
    trackSelectAll,
    summariesByUserId: leaveSummaries.summariesByUserId,
    fetchLocationDeleteSuggestion,
    removeUser: usersQuery.removeUser,
  })
  latest.current = {
    canEditCell,
    getLockReason: permissions.getLockReason,
    getInviteStatus,
    rowSelection,
    handleRowMenuAction,
    optimisticPost,
    trackSelectAll,
    summariesByUserId: leaveSummaries.summariesByUserId,
    fetchLocationDeleteSuggestion,
    removeUser: usersQuery.removeUser,
  }

  const editingContext = useMemo(
    () =>
      createEditingContext({
        teams: teamOptions,
        locations: locationOptions,
        allLabels: reference.labels,
        isAnyRowSelected: () => Object.values(latest.current.rowSelection).some(Boolean),
        canEditCell: (row, columnId) => latest.current.canEditCell(row, columnId),
        getLockReason: (row, columnId) => latest.current.getLockReason(row, columnId),
        optimisticPost: (params) => latest.current.optimisticPost(params),
        patchUser: usersQuery.patchUser,
        locationChangeNote: (firstName: string, locationName: string) => formatMessage({ id: 'users.inlineEdit.locationNote' }, { name: firstName, location: locationName }),
        postEvent: postCoreEvent,
        fetchLocationDeleteSuggestion: latest.current.fetchLocationDeleteSuggestion ? (params) => latest.current.fetchLocationDeleteSuggestion!(params) : undefined,
        suggestLocationDelete: onSuggestLocationDelete,
        onLocationMoved: scopedLocationId
          ? (userId, newLocationId) => {
              if (newLocationId !== scopedLocationId) {
                latest.current.removeUser(userId)
              }
            }
          : undefined,
        onLabelError: (error) => {
          logger.warning({ errorType: 'ERROR TOGGLE LABEL', error })
          showApiErrorNotification(notification, formatMessage, error)
        },
      }),
    [teamOptions, locationOptions, reference.labels, notification, formatMessage, scopedLocationId]
  )

  const rowMenuContext = useMemo(
    () => ({
      companyPlatform: authCompany?.platform,
      userPlatform: authUser.platform,
      isMicrosoftBillingPayment,
      canManageUser: (user: IUserFilteredInfo) => latest.current.canEditCell(user, COLUMN_IDS.actions),
      getInviteStatus: (user: IUserFilteredInfo) => latest.current.getInviteStatus(user),
      isInBulkSelection: (userId: string) => Boolean(latest.current.rowSelection[userId]),
      onAction: (key: RowMenuActionKey, user: IUserFilteredInfo) => latest.current.handleRowMenuAction(key, user),
      // The location column is hidden on a location's own tab, so the menu carries the move instead.
      // commitLocation is the same optimistic path that column used.
      ...(scopedLocationId
        ? {
            locationMove: {
              options: locationOptions.filter((option) => option.id !== scopedLocationId),
              getLockReason: (user: IUserFilteredInfo, action: 'move' | 'status') =>
                latest.current.getLockReason(user, action === 'move' ? COLUMN_IDS.location : COLUMN_IDS.actions),
              onMove: (user: IUserFilteredInfo, targetId: string) => editingContext.commitLocation(user, targetId),
            },
          }
        : {}),
    }),
    [authCompany?.platform, authUser.platform, isMicrosoftBillingPayment, scopedLocationId, locationOptions, editingContext]
  )

  const profileColumns = useMemo(
    () =>
      buildProfileColumns({
        dateFormat,
        companyPlatform: authCompany?.platform,
        showLabels,
        showSelection,
        getInviteStatus: (user: IUserFilteredInfo) => latest.current.getInviteStatus(user),
        approverUserIds: reference.approverUserIds,
        rowMenu: rowMenuContext,
        editing: editingContext,
        moveTargets,
        showEnergy: energyEnabled,
        onSelectAll: () => latest.current.trackSelectAll(),
      }),
    [dateFormat, authCompany?.platform, showLabels, showSelection, reference.approverUserIds, rowMenuContext, editingContext, moveTargets, energyEnabled]
  )

  const activeLeaveTypes = leaveDetails?.activeLeaveTypes ?? []
  const leaveDetailsColumns = useMemo(
    () =>
      buildLeaveDetailsColumns({
        activeLeaveTypes,
        getSummaries: () => latest.current.summariesByUserId,
        companyPlatform: authCompany?.platform,
        getInviteStatus: (user: IUserFilteredInfo) => latest.current.getInviteStatus(user),
        formatMessage,
        rowMenu: rowMenuContext,
        getLockReason: (user, columnId) => latest.current.getLockReason(user, columnId),
        onQuotaEdit: (user, leaveTypeId) => setQuotaEdit({ userId: user.id, leaveTypeId }),
        showEnergy: energyEnabled,
        displayUnit: leaveDetails?.displayUnit ?? 'days',
        showBalance: leaveDetails?.showBalance,
      }),
    [activeLeaveTypes, authCompany?.platform, formatMessage, rowMenuContext, energyEnabled, leaveDetails?.displayUnit, leaveDetails?.showBalance]
  )

  // Derived, not passed: the scope decides the hidden column, so the two cannot disagree.
  const omitted = useMemo(() => new Set<string>(scopedLocationId ? [COLUMN_IDS.location] : []), [scopedLocationId])
  const keepColumn = (id: string) => !omitted.has(id)

  const tableColumnOrder = useMemo(
    () => [COLUMN_IDS.select, COLUMN_IDS.name, ...columnPrefs.columnOrder, COLUMN_IDS.actions].filter(keepColumn),
    [columnPrefs.columnOrder, omitted]
  )
  const leaveTableColumnOrder = useMemo(
    () => [COLUMN_IDS.name, ...(leaveDetails?.columnPrefs.columnOrder ?? []), COLUMN_IDS.actions].filter(keepColumn),
    [leaveDetails?.columnPrefs.columnOrder, omitted]
  )

  // Hidden derivationally, so nothing about this mount is written back to the shared preferences.
  const columnVisibility = useMemo(
    () => ({
      ...columnPrefs.columnVisibility,
      ...Object.fromEntries([...omitted].map((id) => [id, false])),
    }),
    [columnPrefs.columnVisibility, omitted]
  )

  // Text columns size to the rows actually loaded, so a list of short departments stops reserving
  // room for a long one that is not there. Both tabs: `fitColumnWidths` only touches the ids it is
  // handed, so a leave-type column keeps its fixed width either way.
  // The name cell draws an invite badge and an ADP tag beside the name, and neither shrinks. The
  // most decorated row sets what the column has to hold; joined into one key so the memo below
  // compares it by value.
  const nameBadgesKey = users
    .map((user) => {
      const invite = inviteBadgeFor(user, authCompany?.platform, getInviteStatus)
      return [...(invite ? [formatMessage({ id: invite.messageId })] : []), ...(user.adpIntegrated ? [formatMessage({ id: ADP_FLAG_MESSAGE_ID })] : [])]
    })
    .reduce((widest, labels) => (labels.join('').length > widest.join('').length ? labels : widest), [] as string[])
    .join('|')

  const fitWidths = useMemo(() => {
    // Uppercased because that is how the grid draws a header, and it is wider that way.
    const headers = Object.fromEntries(FITTED_TEXT_COLUMN_IDS.map((id) => [id, formatMessage({ id: columnLabelId(id) }).toUpperCase()]))
    const placeholders = {
      [COLUMN_IDS.employeeId]: formatMessage({ id: 'users.table.addValue' }, { field: formatMessage({ id: columnLabelId(COLUMN_IDS.employeeId) }) }),
    }
    const badges = { [COLUMN_IDS.name]: nameBadgesKey ? nameBadgesKey.split('|') : [] }
    return (defaults: Record<string, number>) => fitColumnWidths({ defaults, users, headers, placeholders, badges })
  }, [users, formatMessage, nameBadgesKey])

  const leaveColumnWidths = useMemo(() => fitWidths(leaveDetailsColumnWidths(activeLeaveTypes)), [fitWidths, activeLeaveTypes])
  const profileColumnWidths = useMemo(() => fitWidths(PROFILE_COLUMN_WIDTHS), [fitWidths])
  const leaveNumericColumnIds = useMemo(() => leaveDetailsNumericColumnIds(activeLeaveTypes), [activeLeaveTypes])
  const leaveColumnGroups = useMemo(() => leaveDetailsColumnGroups(activeLeaveTypes), [activeLeaveTypes])

  const bulkLocations = isFullAdmin ? reference.locations.filter((loc) => loc.id !== scopedLocationId) : []
  // Unscoped, one option means every selected user is already there. Scoped, the current location
  // has been filtered out already, so the single remaining option is a valid destination.
  const canChangeLocation = isFullAdmin && bulkLocations.length > (scopedLocationId ? 0 : 1)

  return (
    <UsersTableEditingProvider suspended={selectedCount > 0}>
      <UsersTable
        users={users}
        isLoading={usersQuery.isLoading}
        hasNextPage={usersQuery.hasNextPage}
        isFetchingNextPage={usersQuery.isFetchingNextPage}
        fetchNextPage={usersQuery.fetchNextPage}
        density={density}
        flexColumns="all"
        wrapColumnIds={FITTED_TEXT_COLUMN_IDS}
        {...(view === 'profile'
          ? {
              columns: profileColumns,
              columnOrder: tableColumnOrder,
              columnVisibility,
              columnWidths: profileColumnWidths,
              rowSelection,
              onRowSelectionChange: changeRowSelection,
              canSelectRow,
            }
          : {
              columns: leaveDetailsColumns,
              columnOrder: leaveTableColumnOrder,
              columnVisibility: leaveDetails?.columnPrefs.columnVisibility ?? {},
              columnWidths: leaveColumnWidths,
              pinnedStart: LEAVE_DETAILS_PINNED_START,
              numericColumnIds: leaveNumericColumnIds,
              columnGroups: leaveColumnGroups,
            })}
      />
      {/* The total is the filtered set, the shown count the rows loaded so far. Once paging has
          caught up the two are the same number, and saying it twice reads as a shortfall. With no
          rows at all the empty state already says so, and "Showing 0 users" only repeats it. */}
      {users.length > 0 && (
        <div className="vt-users-count-line vt-users-count-line--centered">
          {totalCount === undefined || users.length >= totalCount ? (
            <IntlMessages id="users.table.rowsShownUsers" values={{ shown: users.length }} />
          ) : (
            <IntlMessages id="users.table.rowsShownOfTotal" values={{ shown: users.length, total: totalCount }} />
          )}
        </div>
      )}
      {showSelection && active && selectedCount > 0 && (
        <BulkActionsBar
          selectedUsers={users.filter((user) => rowSelection[user.id])}
          status={filters.status}
          teams={moveTargets.teams}
          locations={locationMoveTargets(bulkLocations, noCountryLabel)}
          canChangeLocation={canChangeLocation}
          isLocationAdmin={isLocationAdmin}
          managedLocationIds={permissions.managedLocationIds}
          locationLabelId={scopedMoveLabelId}
          scope={selectionScope}
          loadedOnlyNote={loadedOnlyNote}
          onClear={() => changeRowSelection({})}
          onBulkStatusChange={(status) => {
            setConfirmState({
              title: formatMessage({ id: status === 'INACTIVE' ? 'users.table.bulkDeactivateTitle' : 'users.table.bulkActivateTitle' }, { count: selectedCount }),
              message:
                formatMessage({ id: status === 'INACTIVE' ? 'users.table.deactivateConsequenceBulk' : 'users.table.reactivateConsequenceBulk' }, { count: selectedCount }) +
                (loadedOnlyNote ? ` ${loadedOnlyNote}` : ''),
              okLabel: formatMessage({ id: status === 'INACTIVE' ? 'users.table.bulkDeactivateConfirm' : 'users.table.bulkActivateConfirm' }, { count: selectedCount }),
              danger: status === 'INACTIVE',
              onOk: () => changeUsersStatus(status),
            })
          }}
        />
      )}
      {confirmState && (
        <ConfirmModal
          open
          title={confirmState.title}
          message={confirmState.message}
          primaryAction={{
            label: confirmState.okLabel,
            variant: confirmState.danger ? 'primary-danger' : 'primary',
            onClick: () => {
              confirmState.onOk()
              setConfirmState(null)
            },
          }}
          secondaryAction={{ label: formatMessage({ id: 'app.cancel' }), onClick: () => setConfirmState(null) }}
          onClose={() => setConfirmState(null)}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          teams={reference.teams as IGetTeamsShort[]}
          locations={reference.locations as IGetLocationsShort[]}
          isLocationAdmin={isLocationAdmin}
          onClose={() => setEditUser(null)}
          fetchLocationDeleteSuggestion={fetchLocationDeleteSuggestion}
          onSuggestLocationDelete={onSuggestLocationDelete}
        />
      )}
      {quotaEdit && (
        <QuotaEditDialog
          userId={quotaEdit.userId}
          leaveTypeId={quotaEdit.leaveTypeId}
          hourlyLeaveAccounting={Boolean(authCompany?.hourlyLeaveAccounting)}
          dateFormat={dateFormat}
          locale={authUser.locale}
          onClose={() => setQuotaEdit(null)}
          onSaved={(userId) => {
            setTimeout(() => {
              void leaveSummaries.refetchUser(userId)
            }, 3000)
          }}
        />
      )}
    </UsersTableEditingProvider>
  )
}
